import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, UserPlus, UserCheck, UserX, Clock, Ban, CalendarHeart, Users, Images, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { colors } from "@/lib/theme";
import { longDate } from "@/lib/date";
import { Avatar, Spinner } from "@/components/peeps/ui";
import { HomeButton } from "@/components/peeps/HomeButton";

function EventTile({ ev, onOpen }) {
  const photo = ev.photo_url ? mediaUrl(ev.photo_url) : null;
  return (
    <button data-testid={`together-event-${ev.id}`} onClick={onOpen} className="rounded-xl overflow-hidden text-left" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
      <div className="relative w-full" style={{ aspectRatio: "4 / 3", background: colors.surfaceTertiary }}>
        {photo ? (
          <img src={photo} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center"><CalendarHeart size={22} color={colors.muted} /></div>
        )}
      </div>
      <div className="p-2.5">
        <p className="font-display font-bold text-[13px] truncate" style={{ color: colors.onSurface }}>{ev.title}</p>
        <p className="font-display text-[11px]" style={{ color: colors.faint }}>{longDate(ev.date)}</p>
      </div>
    </button>
  );
}

export default function UserProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.userProfile(id));
    } catch (e) {
      toast(e.message || "Couldn't load profile");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id === user?.id) { navigate("/profile", { replace: true }); return; }
    load();
  }, [id, user?.id, load, navigate]);

  const act = async (fn, successMsg) => {
    setBusy(true);
    try { await fn(); if (successMsg) toast(successMsg); await load(); }
    catch (e) { toast(e.message || "Something went wrong"); }
    finally { setBusy(false); }
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen" style={{ background: colors.surface }}><Spinner /></div>;
  }
  if (!data) return null;

  const { user: p, friendship_status: fs, i_blocked: iBlocked, mutual_groups: mutual, upcoming_overlap: overlap,
          events_together: together, photos_together: photos, events_together_count: count } = data;
  const isFriend = fs === "accepted";
  const nextOverlap = overlap && overlap[0];

  const FriendButton = () => {
    if (fs === "blocked") return null;
    if (fs === "accepted") {
      return (
        <button data-testid="friend-remove-button" disabled={busy} onClick={() => act(() => api.removeFriend(id), "Removed friend")}
          className="flex items-center justify-center gap-1.5 flex-1 rounded-xl py-3" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
          <UserCheck size={16} color={colors.brandSecondary} />
          <span className="font-display font-medium text-[13.5px]" style={{ color: colors.onSurface }}>Friends</span>
        </button>
      );
    }
    if (fs === "pending_out") {
      return (
        <button data-testid="friend-cancel-button" disabled={busy} onClick={() => act(() => api.removeFriend(id), "Request cancelled")}
          className="flex items-center justify-center gap-1.5 flex-1 rounded-xl py-3" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
          <Clock size={16} color={colors.muted} />
          <span className="font-display font-medium text-[13.5px]" style={{ color: colors.onSurfaceTertiary }}>Requested</span>
        </button>
      );
    }
    if (fs === "pending_in") {
      return (
        <div className="flex gap-2 flex-1">
          <button data-testid="friend-accept-button" disabled={busy} onClick={() => act(() => api.acceptFriend(id), "You're now friends")}
            className="flex items-center justify-center gap-1.5 flex-1 rounded-xl py-3" style={{ background: colors.brand }}>
            <UserCheck size={16} color={colors.onBrand} />
            <span className="font-display font-bold text-[13.5px]" style={{ color: colors.onBrand }}>Accept</span>
          </button>
          <button data-testid="friend-decline-button" disabled={busy} onClick={() => act(() => api.declineFriend(id), "Request declined")}
            className="flex items-center justify-center rounded-xl py-3 px-4" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
            <UserX size={16} color={colors.onSurfaceTertiary} />
          </button>
        </div>
      );
    }
    // none
    return (
      <button data-testid="friend-add-button" disabled={busy} onClick={() => act(() => api.sendFriendRequest(id), "Friend request sent")}
        className="flex items-center justify-center gap-1.5 flex-1 rounded-xl py-3" style={{ background: colors.brand }}>
        <UserPlus size={16} color={colors.onBrand} />
        <span className="font-display font-bold text-[13.5px]" style={{ color: colors.onBrand }}>Add friend</span>
      </button>
    );
  };

  return (
    <div className="min-h-screen max-w-2xl mx-auto pb-24" style={{ background: colors.surface }}>
      <div className="flex items-center px-3 pt-4 pb-2">
        <button data-testid="profile-back" onClick={() => navigate(-1)} className="rounded-full p-1.5" style={{ background: colors.surfaceSecondary }}>
          <ChevronLeft size={22} color={colors.onSurface} />
        </button>
        <div className="ml-2"><HomeButton size={32} bg={colors.surfaceSecondary} /></div>
      </div>

      {/* Header */}
      <div className="flex flex-col items-center px-4 pt-2">
        <Avatar name={p.display_name} uri={p.profile_photo_url} size={92} />
        <p className="font-display font-bold text-[22px] mt-3" style={{ color: colors.onSurface }}>{p.display_name}</p>
        {p.bio && <p className="font-display text-[13px] text-center mt-1 max-w-sm" style={{ color: colors.onSurfaceTertiary }}>{p.bio}</p>}
        {mutual.length > 0 && (
          <div className="flex items-center gap-1.5 mt-2">
            <Users size={13} color={colors.faint} />
            <span className="font-display text-[12.5px]" style={{ color: colors.faint }}>
              {mutual.length} mutual group{mutual.length === 1 ? "" : "s"} · {mutual.map((g) => g.name).slice(0, 2).join(", ")}{mutual.length > 2 ? "…" : ""}
            </span>
          </div>
        )}
        {isFriend && count > 0 && (
          <p data-testid="events-together-stat" className="font-display font-medium text-[12.5px] mt-1" style={{ color: colors.brandSecondary }}>{count} event{count === 1 ? "" : "s"} together</p>
        )}
      </div>

      {/* Action row */}
      <div className="px-4 mt-5 flex items-center gap-2">
        {fs !== "blocked" && <FriendButton />}
        {isFriend && (
          <button data-testid="message-friend-button" onClick={() => navigate(`/messages/${id}`)}
            className="flex items-center justify-center gap-1.5 flex-1 rounded-xl py-3" style={{ background: colors.brand }}>
            <MessageCircle size={16} color={colors.onBrand} />
            <span className="font-display font-bold text-[13.5px]" style={{ color: colors.onBrand }}>Message</span>
          </button>
        )}
        {fs !== "blocked" && (
          <button data-testid="profile-block-button" disabled={busy} onClick={() => act(() => api.blockUser(id), "Blocked")}
            className="flex items-center justify-center rounded-xl py-3 px-4" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
            <Ban size={16} color={colors.warning} />
          </button>
        )}
        {fs === "blocked" && iBlocked && (
          <button data-testid="profile-unblock-button" disabled={busy} onClick={() => act(() => api.unblockUser(id), "Unblocked")}
            className="flex-1 flex items-center justify-center gap-1.5 rounded-xl py-3" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.warning}` }}>
            <Ban size={16} color={colors.warning} />
            <span className="font-display font-medium text-[13.5px]" style={{ color: colors.warning }}>Unblock</span>
          </button>
        )}
      </div>

      {/* Upcoming overlap banner — strongest lever, shown to friend & non-friend */}
      {nextOverlap && (
        <button data-testid="upcoming-overlap-banner" onClick={() => navigate(`/event/${nextOverlap.id}`)}
          className="mx-4 mt-5 w-[calc(100%-2rem)] rounded-2xl p-4 text-left flex items-center gap-3"
          style={{ background: "linear-gradient(120deg, #2A2350, #3A2E63)", border: `1px solid ${colors.brand}` }}>
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: colors.brand + "33" }}>
            <CalendarHeart size={20} color={colors.brandSecondary} />
          </div>
          <div className="min-w-0">
            <p className="font-display font-bold text-[14px]" style={{ color: "#fff" }}>You're both going</p>
            <p className="font-display text-[12.5px] truncate" style={{ color: "rgba(255,255,255,0.8)" }}>
              {nextOverlap.title} · {longDate(nextOverlap.date)}{nextOverlap.time ? ` · ${nextOverlap.time}` : ""}
            </p>
          </div>
        </button>
      )}

      {/* Friend-only sections */}
      {isFriend ? (
        <>
          {photos && photos.length > 0 && (
            <div className="px-4 mt-7">
              <div className="flex items-center gap-1.5 mb-2.5">
                <Images size={14} color={colors.brandSecondary} />
                <p className="font-display font-bold text-[15px]" style={{ color: colors.onSurface }}>Photos together</p>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {photos.map((ph) => (
                  <div key={ph.id} data-testid={`photo-together-${ph.id}`} className="rounded-lg overflow-hidden" style={{ aspectRatio: "1 / 1", background: colors.surfaceTertiary }}>
                    <img src={mediaUrl(ph.url)} alt={ph.event_title} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="px-4 mt-7">
            <div className="flex items-center gap-1.5 mb-2.5">
              <CalendarHeart size={14} color={colors.brandSecondary} />
              <p className="font-display font-bold text-[15px]" style={{ color: colors.onSurface }}>Events together</p>
            </div>
            {together && together.length > 0 ? (
              <div className="grid grid-cols-2 gap-2.5">
                {together.map((ev) => <EventTile key={ev.id} ev={ev} onOpen={() => navigate(`/event/${ev.id}`)} />)}
              </div>
            ) : (
              <div className="rounded-xl p-5 text-center" style={{ background: colors.surfaceSecondary, border: `1px dashed ${colors.border}` }}>
                <p className="font-display text-[13px]" style={{ color: colors.faint }}>No shared events yet. RSVP to the same plan to start making memories.</p>
              </div>
            )}
          </div>
        </>
      ) : fs !== "blocked" ? (
        <div className="px-4 mt-7">
          <div className="rounded-2xl p-6 text-center" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: colors.brand + "22" }}>
              <UserPlus size={22} color={colors.brandSecondary} />
            </div>
            <p className="font-display font-bold text-[15px]" style={{ color: colors.onSurface }}>Add {p.display_name.split(" ")[0]} to see more</p>
            <p className="font-display text-[13px] mt-1" style={{ color: colors.onSurfaceTertiary }}>Become friends to see the events and photos you've shared.</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
